import { apiClient } from '@shared/api';
import type { ApiResponse, PagedResult } from '@shared/types';
import {
  type DocStreamEvent,
  type DocumentListItem,
  type DocumentRegistryQuery,
  type DocumentView,
  type FilePreflightResult,
  type IntakeToday,
  type NsPreview,
  type ShareLink,
  type ReviewQueueItem,
  type ReviewQueueQuery,
  type BulkRejectResult,
  type ScanUploadResult,
  type TokenResolve,
  type VerificationStats,
  type VerifierConfig,
  type VerifierConfigUpdate,
  type VerifierStatus,
} from '../types/documentCapture.types';

const BASE = '/document-capture';

/**
 * API origin for the few calls that must bypass `apiClient` (the token resolve). Mirrors the client's
 * own base so dev proxying and the production reverse proxy both keep working.
 */
const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');

/**
 * Preflight/commit decode the PDF synchronously and share the watcher lane's decode pool
 * (2 workers, 120s wall-clock each — decided 2026-07-27, T7). This bound guarantees the panel's
 * checking state always resolves: slightly past the server's own kill switch, so a queued or
 * pathological decode lands on the retry row instead of an eternal spinner.
 */
const UPDATE_DECODE_TIMEOUT_MS = 150_000;

/** Drops undefined/empty values so an untouched filter never appears in the URL (keeps it shareable). */
function toParams(query: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      // ASP.NET binds a repeated key to string[] — types=OP&types=DV
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  }
  return params;
}

function unwrap<T>(data: ApiResponse<T>): T {
  if (data.success && data.data !== undefined && data.data !== null) return data.data;
  throw new Error(data.message || 'Request failed');
}

class DocumentCaptureService {
  // ---------------------------------------------------------------- registry + viewer

  /**
   * The registry page.
   *
   * `signal` is not optional decoration (86d3z56ma A3): with an identification filter applied this is
   * a multi-second query, and until now a request the browser walked away from KEPT RUNNING on the
   * host — the server had no cancellation token on this path at all. Superseding a fetch now aborts
   * the one it replaces, on both ends.
   */
  async getDocuments(
    query: DocumentRegistryQuery,
    signal?: AbortSignal
  ): Promise<PagedResult<DocumentListItem>> {
    const { data } = await apiClient.get<ApiResponse<PagedResult<DocumentListItem>>>(
      `${BASE}/documents?${toParams(query as Record<string, unknown>)}`,
      { signal }
    );
    return unwrap(data);
  }

  /**
   * One document. A 404 means "not available to you" and deliberately does NOT distinguish
   * missing from forbidden — callers must render the same neutral state either way, or the API
   * becomes a way to discover that a treasury document exists.
   */
  async getDocument(docNumber: string): Promise<DocumentView> {
    const { data } = await apiClient.get<ApiResponse<DocumentView>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}`
    );
    return unwrap(data);
  }

  /** Original PDF URL. Inline by default; `download` forces a save dialog. */
  fileUrl(docNumber: string, version?: number, download = false): string {
    const params = toParams({ v: version, download: download || undefined });
    const qs = params.toString();
    return `${BASE}/documents/${encodeURIComponent(docNumber)}/file${qs ? `?${qs}` : ''}`;
  }

  // ---------------------------------------------------------------- review

  /**
   * The full open review queue in one call (no `kind` filter — it's nullable server-side).
   *
   * ⚠️ Deliberately NOT filtered by the server's `Kind`: that field mis-labels 4 of 6
   * NetSuite-side reasons as `Scan` (86d3rrfdk review), so the FE groups by {@link ReviewQueueItem.reason}
   * into the three display tabs instead. One fetch feeds the tabs, the per-tab counts, and the badge.
   *
   * 🐞 `background: true` (86d3z2nz4). This runs on MOUNT, for everyone who opens the capture page —
   * so a user without `docscan.review` got an unsolicited "Access Denied" toast on every single page
   * load, for something they never clicked. Nothing else about the failure changes: the store still
   * sets `reviewError`, and the Needs-review card still renders it inline with a Try again.
   */
  async getReviewQueue(query: ReviewQueueQuery = {}): Promise<PagedResult<ReviewQueueItem>> {
    const { data } = await apiClient.get<ApiResponse<PagedResult<ReviewQueueItem>>>(
      `${BASE}/review?${toParams(query as Record<string, unknown>)}`,
      { background: true }
    );
    return unwrap(data);
  }

  /**
   * Live NetSuite candidates — always re-queried, never replayed from a stored snapshot.
   *
   * ⚠️ Every call runs a real SuiteQL query server-side (`NsDocLookupService.LookupAsync`), which is
   * ungated: no semaphore, no rate limit, no in-flight cap. The account's ceiling is 5 concurrent
   * across 8 integrations and is already oversubscribed, so a caller that can fire this in a loop
   * MUST debounce and MUST pass `signal` — dropping a superseded result is not enough, because the
   * request still completes and still holds a slot. This is the shape of the 2026-07-29 starvation
   * incident. See {@link useNetSuiteFacts} for the reference caller.
   */
  async previewNetSuite(docNumber: string, signal?: AbortSignal): Promise<NsPreview> {
    const { data } = await apiClient.get<ApiResponse<NsPreview>>(
      `${BASE}/netsuite/preview/${encodeURIComponent(docNumber)}`,
      { signal }
    );
    return unwrap(data);
  }

  /**
   * Page image of the scan behind a review item — the only way to show an UNATTACHED scan (the
   * Scans tab), which has no document number. Returns a URL the browser fetches with its bearer
   * token via the apiClient base, so this is a path the `<img>`/canvas requests through apiClient,
   * not a raw string. (Callers fetch it as a blob to keep the auth header.)
   */
  async getReviewItemPageBlob(reviewItemId: number, page: number): Promise<string> {
    const { data } = await apiClient.get<Blob>(`${BASE}/review/${reviewItemId}/pages/${page}`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(data);
  }

  /**
   * The original PDF behind a review item (86d3w2jrj FE-1). Page images are produced during a
   * decode, so an unreadable scan — the one a reviewer most needs to look at — has none. This is how
   * they see the paper anyway. Opens in a new tab; the object URL is revoked on a timer because
   * revoking it immediately would cancel the tab's own load.
   */
  async openReviewItemPdf(reviewItemId: number): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/review/${reviewItemId}/file`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(data);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  // ---------------------------------------------------------------- review decisions
  //
  // Every decision echoes the document's linkEpoch so a stale one 409s — EXCEPT assign, whose
  // file has no document yet (nothing to be stale against). 403 is handled globally (the apiClient
  // interceptor toasts "Access Denied"); callers handle 409 (drift) and 400 (not-applicable).

  async assign(reviewItemId: number, docNumber: string): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/assign`, { docNumber });
  }

  /**
   * 86d45ffw4 — the reviewer pasted the document's link onto the NetSuite record themselves and is
   * saying so. Records the claim and resolves the item; DOP writes nothing to NetSuite here, and the
   * nightly sweep is what turns the claim into a plain Linked.
   */
  async confirmManualLink(reviewItemId: number, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/confirm-manual-link`, { linkEpoch });
  }

  async keepWatching(reviewItemId: number, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/keep-watching`, { linkEpoch });
  }

  /**
   * Reject a batch of UNIDENTIFIED scans (86d3x48q7) — the junk sweep. Per-item on the server, so a
   * stale item fails alone instead of rolling back the batch; the result names what did not go
   * through. There is no bulk APPROVE of any kind, deliberately.
   */
  async bulkRejectScans(reviewItemIds: number[], reason: string): Promise<BulkRejectResult> {
    const { data } = await apiClient.post<ApiResponse<BulkRejectResult>>(`${BASE}/review/bulk-reject`, {
      reviewItemIds,
      reason,
    });
    return unwrap(data);
  }

  /**
   * Confirm a spot check (86d3x48q7 T13) — the reviewer compared an auto-linked scan against the
   * paper and it matches. Changes NOTHING about the document; the value is the resolution stamp,
   * which is the only measurement of auto-link precision that exists. The "doesn't match" answer is
   * {@link reject} with `unlink: true`.
   */
  async confirmSpotCheck(reviewItemId: number, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/confirm-spot-check`, { linkEpoch });
  }

  async reject(reviewItemId: number, reason: string, linkEpoch: number, unlink = false): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/reject`, {
      reason,
      linkEpoch,
      unlink,
    });
  }

  async resolveConflict(reviewItemId: number, useDop: boolean, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/resolve-conflict`, {
      useDop,
      linkEpoch,
    });
  }

  async decideReplacement(reviewItemId: number, approve: boolean, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/replacement`, {
      approve,
      linkEpoch,
    });
  }

  /** 86d3vbwup W9 — discard an UNREADABLE replacement upload (DecodeFailed terminal card). */
  async discardUpload(reviewItemId: number, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/discard-upload`, {
      linkEpoch,
    });
  }

  /**
   * 86d3vbwup W9 — the mismatch escape hatch: file the held upload against the document its barcode
   * actually reads (the item's `reasonValue`). The server refuses without view-permission on the
   * TARGET family; the FE hides the action for the same callers.
   */
  async refileUpload(reviewItemId: number, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/refile`, {
      linkEpoch,
    });
  }

  async resolveMultiMatch(reviewItemId: number, nsInternalId: string, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/review/${reviewItemId}/resolve-multi-match`, {
      nsInternalId,
      linkEpoch,
    });
  }

  // ---------------------------------------------------------------- file update lane (T7)
  //
  // The deliberate download → edit → re-upload path. All multipart calls force the multipart
  // Content-Type (the shared apiClient defaults to JSON, which 415s IFormFile binding — the
  // 86d3rrfee gotcha). Errors: 400 carries the server's plain-language reason, 409 = the document
  // changed (refetch + re-preflight), timeouts land on the panel's retry row.

  /** Decode a candidate replacement file WITHOUT committing — drives the pre-commit check list. */
  async preflightFile(docNumber: string, file: File): Promise<FilePreflightResult> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<ApiResponse<FilePreflightResult>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}/file/preflight`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPDATE_DECODE_TIMEOUT_MS }
    );
    return unwrap(data);
  }

  /**
   * 86d3vbwup W9 — ACCEPT the edited file (async): returns quickly with the fresh view containing the
   * new PROVISIONAL version (`checking`); the decode + match gate run in the background and resolve
   * to promoted / held / failed. 409 = the document changed (refetch + re-preflight), 400 = the
   * server's plain-language refusal (duplicates, unreadable shape).
   */
  async updateFile(docNumber: string, file: File, linkEpoch: number, note?: string): Promise<DocumentView> {
    const form = new FormData();
    form.append('file', file);
    form.append('linkEpoch', String(linkEpoch));
    if (note) form.append('note', note);
    const { data } = await apiClient.post<ApiResponse<DocumentView>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}/file`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPDATE_DECODE_TIMEOUT_MS }
    );
    return unwrap(data);
  }

  /** Copy an earlier version forward as a NEW current version (never rewrites history). */
  async restoreVersion(docNumber: string, version: number, linkEpoch: number): Promise<DocumentView> {
    const { data } = await apiClient.post<ApiResponse<DocumentView>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}/restore/${version}`,
      { linkEpoch }
    );
    return unwrap(data);
  }

  // ---------------------------------------------------------------- public share links (T8)
  //
  // Staff-side minting/revoking of the customer link. Requires docscan.share. `getShareLink` and
  // `revokeShareLink` can legitimately return null (no active link), so they read `data` directly
  // rather than through the throw-on-null `unwrap`.

  /** Get-or-create the active public link for a document. */
  async createShareLink(docNumber: string): Promise<ShareLink> {
    const { data } = await apiClient.post<ApiResponse<ShareLink>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}/share`
    );
    return unwrap(data);
  }

  /** The active link + trail, or null when the document is visible but not yet shared. */
  async getShareLink(docNumber: string): Promise<ShareLink | null> {
    const { data } = await apiClient.get<ApiResponse<ShareLink | null>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}/share`
    );
    return data.success ? (data.data ?? null) : null;
  }

  /**
   * 14ykpqtwauu — revoke the active link WITHOUT replacing it (the URL dies on the next request). The
   * leak response for when Replace is refused. Local-only, so NetSuite cannot refuse it; epoch-fenced
   * like rotate (a stale screen answers 409).
   */
  async revokeShareLink(docNumber: string, linkEpoch: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}/share/revoke`,
      null,
      { params: { expectedEpoch: linkEpoch } }
    );
  }

  /**
   * Replace the link AND re-point NetSuite, atomically (86d3uu104, T10). Replaces the old two-call
   * "regenerate": those were independent requests, so a revoke that succeeded followed by a create
   * that failed left the dialog showing a dead URL as though it were live.
   *
   * A NetSuite refusal (closed period) comes back as a 400 whose message is the verbatim NetSuite
   * reason, and NOTHING has changed — the current link still works.
   */
  async rotateShareLink(docNumber: string, expectedEpoch: number): Promise<ShareLink | null> {
    const { data } = await apiClient.post<ApiResponse<ShareLink | null>>(
      `${BASE}/documents/${encodeURIComponent(docNumber)}/share/rotate`,
      null,
      { params: { expectedEpoch } }
    );
    return data.success ? (data.data ?? null) : null;
  }

  /**
   * Resolve a capability token to its document, as a signed-in user (86d3uu104, T10).
   *
   * ⚠️ Bare `fetch`, NOT `apiClient`. The shared client's 401 handler clears tokens and hard-navigates
   * to /login — on a `/p/` link that would strand a customer on a staff login page and destroy the
   * return path. Returns null for 401/403/404 alike (the server answers one neutral 404 for "unknown
   * token" and "not yours"), and THROWS on 5xx/network so the caller can retry rather than silently
   * downgrading a permitted staffer to the customer view.
   */
  async resolveToken(token: string, accessToken: string | null): Promise<TokenResolve | null> {
    const res = await fetch(`${API_ORIGIN}${BASE}/resolve/${encodeURIComponent(token)}`, {
      headers: {
        Accept: 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });
    if (res.status === 401 || res.status === 403 || res.status === 404) return null;
    if (!res.ok) throw new Error(`resolve failed: ${res.status}`);
    const body = (await res.json()) as ApiResponse<TokenResolve>;
    return body.success ? (body.data ?? null) : null;
  }

  // ---------------------------------------------------------------- intake

  /**
   * One scan in. Requires `docscan.upload`. The answer is synchronous and honest about
   * duplicates — it carries the EXISTING document's current status, so the journey row can say
   * "already captured (Linked)" without a second request.
   */
  async uploadScan(file: File, stationId?: string, clientUploadId?: string): Promise<ScanUploadResult> {
    const form = new FormData();
    form.append('file', file);
    if (stationId) form.append('stationId', stationId);
    // Correlation id (86d3uf18j) — the server persists it and echoes it in the live stream, so the
    // journey row reconciles even if THIS response never arrives (timeout / dropped connection).
    if (clientUploadId) form.append('clientUploadId', clientUploadId);
    // The shared apiClient defaults Content-Type to application/json; sending FormData under that
    // makes the API's IFormFile binder reject the upload with 415. Force multipart so axios sets the
    // boundary from the FormData. (86d3rrfee — intake was 415-looping the watcher.)
    const { data } = await apiClient.post<ApiResponse<ScanUploadResult>>(`${BASE}/scans`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(data);
  }

  /** Scan Station keep-alive (~60s). Requires `docscan.upload`. */
  async heartbeat(stationId: string, filesToday: number): Promise<void> {
    await apiClient.post<ApiResponse<null>>(`${BASE}/station/heartbeat`, { stationId, filesToday });
  }

  // ---- second reader admin (86d3x48q7 T13). All three require `docscan.ai_manage`. ----
  //
  // 86d3yeh4j adds a FOURTH call below that deliberately does NOT: `getVerifierStatus` is open to any
  // authenticated user of the capture page, because it drives the outage banner and the person who
  // needs that banner is the one at the scanner, not the administrator.

  async getVerifierConfig(): Promise<VerifierConfig> {
    const { data } = await apiClient.get<ApiResponse<VerifierConfig>>(`${BASE}/admin/verifier-config`);
    return unwrap(data);
  }

  /**
   * PARTIAL update — send only what changed, and reset the dirty baseline from what comes back.
   * There is no row versioning anywhere in this codebase, so a full-object PUT from a stale form
   * would quietly overwrite a change another admin made between the read and the write.
   */
  async updateVerifierConfig(update: VerifierConfigUpdate): Promise<VerifierConfig> {
    const { data } = await apiClient.put<ApiResponse<VerifierConfig>>(`${BASE}/admin/verifier-config`, update);
    return unwrap(data);
  }

  async getVerificationStats(): Promise<VerificationStats> {
    const { data } = await apiClient.get<ApiResponse<VerificationStats>>(`${BASE}/admin/verification-stats`);
    return unwrap(data);
  }

  /**
   * 86d3yeh4j — is the second reader up, and how many scans are parked behind it?
   *
   * NOT under `/admin` and NOT gated on `docscan.ai_manage`, unlike its neighbours: the banner this
   * feeds exists for the operator whose scans stopped coming back. It discloses only up/down, two
   * counts and a typed failure class — no document, customer or request detail.
   */
  async getVerifierStatus(): Promise<VerifierStatus> {
    const { data } = await apiClient.get<ApiResponse<VerifierStatus>>(`${BASE}/verifier-status`);
    return unwrap(data);
  }

  /**
   * "It's fixed — retry now": close the breaker and release every held scan immediately. Gated on
   * `docscan.ai_manage` — seeing that the reader is down is one thing, asserting it is fixed is
   * another. Returns the post-action status, so the caller never renders a stale "N waiting".
   */
  async retryVerifierNow(): Promise<VerifierStatus> {
    const { data } = await apiClient.post<ApiResponse<VerifierStatus>>(`${BASE}/admin/verifier-retry-now`);
    return unwrap(data);
  }

  // ---------------------------------------------------------------- live stream

  /** Replay of the last events, so a page that just opened isn't blank until something happens. */
  async getRecentEvents(max = 50): Promise<DocStreamEvent[]> {
    const { data } = await apiClient.get<ApiResponse<DocStreamEvent[]>>(
      `${BASE}/stream/recent?max=${max}`
    );
    return unwrap(data);
  }

  // ---------------------------------------------------------------- today's intake (86d3yek7d)

  /**
   * Today's intake, counted in the database and scoped to what this caller may see — the source the
   * "Today" card, the tally strip and the station pill read instead of per-tab counters and the
   * stream's ~50-event replay window.
   *
   * Authenticated but NOT permission-gated, like `getVerifierStatus` above: the scoping is inside the
   * query, so a user with no families simply sees their own uploads. Passing the browser's station id
   * adds that station's own count for the header pill.
   */
  async getIntakeToday(stationId?: string): Promise<IntakeToday> {
    const query = stationId ? `?${new URLSearchParams({ stationId })}` : '';
    const { data } = await apiClient.get<ApiResponse<IntakeToday>>(`${BASE}/stats/intake-today${query}`);
    return unwrap(data);
  }
}

export const documentCaptureService = new DocumentCaptureService();
